import { RetellWebCallResponse } from '../../api/bookingAgent';

export interface RetellStartCallConfig {
  accessToken: string;
  callId?: string;
  transport?: 'gateway' | 'livekit';
  iceServers?: RTCIceServer[];
}

/**
 * Map a v3 create-web-call payload onto Retell SDK 3 startCall options.
 * transport and iceServers are left off when the backend does not send them.
 */
export function toRetellStartCallConfig(
  response: RetellWebCallResponse,
): RetellStartCallConfig {
  const config: RetellStartCallConfig = {
    accessToken: response.access_token,
  };
  const callId = nonEmptyString(response.call_id);
  if (callId) {
    config.callId = callId;
  }
  const transport = retellTransport(response.transport);
  if (transport) {
    config.transport = transport;
  }
  const iceServers = toIceServers(response.ice_servers);
  if (iceServers.length > 0) {
    config.iceServers = iceServers;
  }
  return config;
}

function nonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function retellTransport(value: unknown): 'gateway' | 'livekit' | undefined {
  const transport = nonEmptyString(value);
  if (!transport) {
    return undefined;
  }
  const normalized = transport.toLowerCase();
  if (normalized === 'gateway' || normalized === 'livekit') {
    return normalized;
  }
  return undefined;
}

function toIceServers(value: unknown): RTCIceServer[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const servers: RTCIceServer[] = [];
  value.forEach((entry) => {
    const server = toIceServer(entry);
    if (server) {
      servers.push(server);
    }
  });
  return servers;
}

function toIceServer(entry: unknown): RTCIceServer | undefined {
  if (!entry || typeof entry !== 'object') {
    return undefined;
  }
  const record = entry as Record<string, unknown>;
  const urls = iceServerUrls(record.urls !== undefined ? record.urls : record.url);
  if (!urls) {
    return undefined;
  }
  const server: RTCIceServer = { urls };
  const username = iceAuthValue(record.username);
  const credential = iceAuthValue(record.credential);
  if (username) {
    server.username = username;
  }
  if (credential) {
    server.credential = credential;
  }
  return server;
}

function iceAuthValue(value: unknown): string | undefined {
  if (typeof value !== 'string' || value === '') {
    return undefined;
  }
  return value;
}

function iceServerUrls(value: unknown): string | string[] | undefined {
  const single = nonEmptyString(value);
  if (single) {
    return single;
  }
  if (!Array.isArray(value)) {
    return undefined;
  }
  const urls = value
    .map((entry) => nonEmptyString(entry))
    .filter((entry): entry is string => Boolean(entry));
  if (urls.length === 0) {
    return undefined;
  }
  return urls;
}
